import { useState } from "react";
import { Eye, User, MapPin, Package, Truck, Loader2, CreditCard, Hash, Calendar } from "lucide-react";
import { api, formatUSD, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const PAY_STYLE = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  initiated: "bg-slate-100 text-slate-600",
  expired: "bg-slate-100 text-slate-500",
  failed: "bg-brand-red/10 text-brand-red",
};
const SHIP_STYLE = {
  processing: "bg-amber-100 text-amber-700",
  shipped: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
};

const Badge = ({ className, children }) => (
  <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold capitalize ${className}`}>{children}</span>
);

function AddressBlock({ addr }) {
  if (!addr || !(addr.line1 || addr.city)) {
    return <p className="text-brand-ink2 text-sm">No shipping address on file.</p>;
  }
  const cityLine = [addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ");
  return (
    <address className="not-italic text-brand-navy leading-relaxed" data-testid="order-detail-address">
      {addr.line1 && <div>{addr.line1}</div>}
      {addr.line2 && <div>{addr.line2}</div>}
      {cityLine && <div>{cityLine}</div>}
      {addr.country && <div className="text-brand-ink2">{addr.country}</div>}
    </address>
  );
}

export function OrderDetailDialog({ order }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) {
      setLoading(true);
      setDetail(null);
      try {
        const { data } = await api.get(`/admin/orders/${order.session_id}`);
        setDetail(data);
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail, "Could not load order."));
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const d = detail || order;
  const items = d.items || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <button
          data-testid={`order-view-btn-${order.session_id}`}
          className="btn-focus inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-brand-line text-sm font-semibold text-brand-navy hover:border-brand-navy hover:bg-brand-bg transition-colors"
        >
          <Eye className="w-4 h-4 text-brand-red" /> View
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-navy">Order detail</DialogTitle>
          <DialogDescription className="text-brand-ink2">
            Customer, shipping address and itemized breakdown for this order.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-14 grid place-items-center">
            <Loader2 className="w-8 h-8 text-brand-red animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* meta row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
              <span className="inline-flex items-center gap-1.5 text-brand-ink2">
                <Calendar className="w-4 h-4" /> {fmtDate(d.created_at)}
              </span>
              <span className="inline-flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-brand-ink2" />
                <Badge className={PAY_STYLE[d.payment_status] || "bg-slate-100 text-slate-600"}>{d.payment_status || "—"}</Badge>
              </span>
              <span className="inline-flex items-center gap-2">
                <Truck className="w-4 h-4 text-brand-ink2" />
                <Badge className={SHIP_STYLE[d.ship_status || "processing"]}>{d.ship_status || "processing"}</Badge>
              </span>
            </div>

            {/* customer + shipping */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-brand-bg border border-brand-line p-4">
                <p className="font-bold text-brand-navy flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-brand-red" /> Customer
                </p>
                <div className="text-brand-navy text-sm space-y-0.5" data-testid="order-detail-customer">
                  <div className="font-semibold">{d.customer_name || "—"}</div>
                  {d.customer_email
                    ? <a href={`mailto:${d.customer_email}`} className="block text-brand-navy hover:text-brand-red break-all">{d.customer_email}</a>
                    : <div className="text-brand-ink2">No email captured</div>}
                  {d.customer_phone && <a href={`tel:${d.customer_phone}`} className="block text-brand-ink2 hover:text-brand-red">{d.customer_phone}</a>}
                </div>
              </div>
              <div className="rounded-2xl bg-brand-bg border border-brand-line p-4">
                <p className="font-bold text-brand-navy flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-brand-red" /> Shipping address
                </p>
                <AddressBlock addr={d.shipping_address} />
              </div>
            </div>

            {/* items */}
            <div className="rounded-2xl bg-white border border-brand-line overflow-hidden">
              <p className="font-bold text-brand-navy flex items-center gap-2 px-4 py-3 border-b border-brand-line">
                <Package className="w-4 h-4 text-brand-red" /> Items
              </p>
              <table className="w-full text-sm" data-testid="order-detail-items">
                <thead>
                  <tr className="bg-brand-bg text-brand-navy">
                    <th className="text-left font-bold px-4 py-2">Item</th>
                    <th className="text-center font-bold px-4 py-2">Qty</th>
                    <th className="text-right font-bold px-4 py-2">Unit</th>
                    <th className="text-right font-bold px-4 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-4 text-center text-brand-ink2">No items recorded.</td></tr>
                  )}
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-brand-line">
                      <td className="px-4 py-2.5 font-semibold text-brand-navy">{it.name}</td>
                      <td className="px-4 py-2.5 text-center text-brand-navy">{it.quantity}</td>
                      <td className="px-4 py-2.5 text-right text-brand-ink2">{it.unit_price != null ? formatUSD(it.unit_price) : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-brand-navy">{it.line_total != null ? formatUSD(it.line_total) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-brand-line bg-brand-bg">
                    <td colSpan={3} className="px-4 py-3 text-right font-bold text-brand-navy">Order total</td>
                    <td className="px-4 py-3 text-right font-display font-black text-brand-navy" data-testid="order-detail-total">{formatUSD(d.amount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* fulfillment meta */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
              {d.tracking_number && (
                <span className="inline-flex items-center gap-1.5 text-brand-navy font-semibold">
                  <Truck className="w-4 h-4 text-brand-red" /> Tracking:
                  <span className="font-mono text-brand-ink2">{d.tracking_number}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-brand-ink2 sm:ml-auto">
                <Hash className="w-3.5 h-3.5" /> <span className="font-mono break-all">{d.session_id}</span>
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
